import {
  createDataEncryptionKey,
  decryptMnemonic,
  encryptMnemonic,
  encryptUVD,
  getEncUVD,
  makeHint,
  makeUVD,
} from '@wepin/utils'
import BigNumber from 'bignumber.js'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import {
  REFRESH_STATUS_DEFAULT,
  REFRESH_STATUS_DISABLED,
  consecusiveReqDelay,
} from '@/const'
import LoadNonceError from '@/erros/LoadNonceError'
import {
  useWalletAPIStore,
  useAccountAPIStore,
  useAccountTokenAPIStore,
  useNFTAPIStore,
  useUserAPIStore,
  useSettingsStore,
  type APIStoreDeleteWalletOptions,
  useCoinAPIStore,
} from '@/stores'
import {
  type Maybe,
  type Wallet,
  WalletFactory,
  AccountToken,
  AccountTokenFactory,
  NftFactory,
  type Nft,
  Account,
  AccountFactory,
  type BaseAccount,
} from '@/types'
import type {
  IWallet,
  IAccount,
  IAccountToken,
  INFT,
  ICreateWalletRequest,
  IVerifyCodeRequest,
} from '@/types/api'
export type GetAccountListFilteringByNetworkParams = {
  network: string
  force?: boolean
}

const useWalletStore = defineStore('walletStore', () => {
  const walletAPIStore = useWalletAPIStore()
  const accountAPIStore = useAccountAPIStore()
  const coinAPIStore = useCoinAPIStore()
  const accountTokenAPIStore = useAccountTokenAPIStore()
  const nftAPIStore = useNFTAPIStore()
  const userAPIStore = useUserAPIStore()
  const settingStore = useSettingsStore()

  const mnemonic = ref('')
  const tempFullAccountList = ref<BaseAccount[]>()
  const tempGroupAccountList = ref<BaseAccount[]>()
  const selectedWalletId = ref<string>('')

  function setMnemonic(walletMnemonic: string) {
    mnemonic.value = walletMnemonic
  }

  function getMnemonic() {
    return mnemonic.value
  }

  const convertWalletList = (apiWalletList: Maybe<IWallet[]>) => {
    if (!apiWalletList) return []
    return apiWalletList.map((apiWallet) => {
      const wallet = WalletFactory.getInstance().makeWallet(apiWallet)
      return wallet
    })
  }

  const walletList = computed<Wallet[]>(() => {
    return convertWalletList(walletAPIStore.walletList)
  })

  const convertAccountList = (apiAccountList: Maybe<IAccount[]>) => {
    if (!apiAccountList) return []
    // 테스트넷 필터링
    if (coinAPIStore.filterCoinList && coinAPIStore.filterCoinList.length > 0) {
      const filterNetworks = coinAPIStore.filterCoinList.map((coin) =>
        coin.network?.toLowerCase(),
      )
      apiAccountList = apiAccountList.filter((account) => {
        const accountNetwork = account.currencyId?.toLowerCase()
        return !filterNetworks.some(
          (network) => network && accountNetwork?.includes(network),
        )
      })
    }

    return apiAccountList.map((apiAccount) => {
      const account = AccountFactory.getInstance().makeAccount(apiAccount)
      return account
    })
  }

  const accountList = computed<Account[]>(() => {
    return convertAccountList(accountAPIStore.accountList)
  })

  const groupAccountList = computed<BaseAccount[]>(() => {
    if (!selectedWalletId.value) return []
    if (tempGroupAccountList.value?.length) {
      return tempGroupAccountList.value
    }
    const accounts = accountList.value.filter(
      (account) => account.walletId === selectedWalletId.value,
    )
    const seenIds = new Set<string>()
    const uniqueAccounts = accounts.filter((account) => {
      if (seenIds.has(account.id)) {
        return false
      }
      seenIds.add(account.id)
      return true
    })
    return (uniqueAccounts ?? []) as BaseAccount[]
  })
  const fullAccountList = computed<BaseAccount[]>(() => {
    if (!selectedWalletId.value) return []
    if (tempFullAccountList.value?.length) {
      return tempFullAccountList.value
    }
    const fullList: (Account | AccountToken)[] = []
    const seenIds = new Set<string>()
    const accounts = accountList.value.filter(
      (account) => account.walletId === selectedWalletId.value,
    )

    accounts?.forEach((account) => {
      if (!seenIds.has(account.id)) {
        seenIds.add(account.id)
        fullList.push(account)
      }
      if (!account.tokens?.length) return
      account.tokens.forEach((token) => {
        if (!seenIds.has(token.id)) {
          seenIds.add(token.id)
          fullList.push(token)
        }
      })
    })
    return (fullList ?? []) as BaseAccount[]
  })

  const convertAccountTokenList = (
    apiAccountTokenList: Maybe<IAccountToken[]>,
  ) => {
    const accountTokenList: AccountToken[] = []
    if (!apiAccountTokenList) return accountTokenList

    return apiAccountTokenList.map((accountToken) => {
      return AccountTokenFactory.getInstance().makeAccountToken(accountToken)
    })
  }
  const accountTokenList = computed<AccountToken[]>(() => {
    return convertAccountTokenList(accountTokenAPIStore.accountTokenList)
  })

  const sortAccountList = (
    sortBy: 'name' | 'balance' | 'recent',
    mapperObj: { [key: string]: string[] },
  ) => {
    switch (sortBy) {
      case 'name':
        if (settingStore.accountLayout.group) {
          tempGroupAccountList.value = groupAccountList.value
          tempGroupAccountList.value?.sort((accountA, accountB) => {
            return accountA.label[0].localeCompare(accountB.label[0], 'en')
          })
        } else {
          tempFullAccountList.value = fullAccountList.value
          tempFullAccountList.value.sort((accountA, accountB) => {
            return accountA.label[0].localeCompare(accountB.label[0], 'en')
          })
        }
        break
      case 'balance':
        if (settingStore.accountLayout.group) {
          tempGroupAccountList.value = groupAccountList.value
          tempGroupAccountList.value?.sort((accountA, accountB) => {
            if (
              new BigNumber(accountB.amount.weiValue).gt(
                new BigNumber(accountA.amount.weiValue),
              )
            )
              return 1
            else if (
              new BigNumber(accountB.amount.weiValue).eq(
                new BigNumber(accountA.amount.weiValue),
              )
            )
              return 0
            else return -1
          })
        } else {
          tempFullAccountList.value = fullAccountList.value
          tempFullAccountList.value.sort((accountA, accountB) => {
            if (
              new BigNumber(accountB.amount.weiValue).gt(
                new BigNumber(accountA.amount.weiValue),
              )
            )
              return 1
            else if (
              new BigNumber(accountB.amount.weiValue).eq(
                new BigNumber(accountA.amount.weiValue),
              )
            )
              return 0
            else return -1
          })
        }
        break
      case 'recent':
        if (settingStore.accountLayout.group) {
          tempGroupAccountList.value = groupAccountList.value
          tempGroupAccountList.value?.sort((accountA, accountB) => {
            if (
              mapperObj[accountB.getKey()][0] > mapperObj[accountA.getKey()][0]
            )
              return 1
            else if (
              mapperObj[accountB.getKey()][0] < mapperObj[accountA.getKey()][0]
            )
              return -1
            else return 0
          })
        } else {
          tempFullAccountList.value = fullAccountList.value
          tempFullAccountList.value.sort((accountA, accountB) => {
            if (
              mapperObj[accountB.getKey()][0] > mapperObj[accountA.getKey()][0]
            )
              return 1
            else if (
              mapperObj[accountB.getKey()][0] < mapperObj[accountA.getKey()][0]
            )
              return -1
            else return 0
          })
        }
    }
  }
  const convertNftList = (apiNftList: Maybe<INFT[]>) => {
    const nfts: Nft[] = []
    if (!apiNftList) return nfts
    return apiNftList.map((nft) => {
      return NftFactory.getInstance().makeNft(nft)
    })
  }
  const nftList = computed(() => {
    const apiNftList = nftAPIStore.nftList
    return convertNftList(apiNftList)
  })
  const getNft = (nftId: string) => {
    return nftList.value.find((nft) => nft.id === nftId)
  }

  /**
   * PIN 을 암호화해서 UVD 로 만들어주는 함수
   *
   * @param pin
   *
   */
  const encryptPIN = async (pin: string, nonce: number) => {
    const uvd = makeUVD(pin)
    if (!uvd) throw new Error('invalid pin')
    const b64PubKey = await userAPIStore.getUserPubKey()
    if (!b64PubKey) throw new Error('No Publick Key')
    const encUvd = await getEncUVD(b64PubKey.pubKey, {
      UVD: uvd,
      nonce,
    })
    return encUvd
  }

  const encryptPINIncluedDEK = async (pin: string, nonce: number) => {
    const uvd = makeUVD(pin)
    if (!uvd) throw new Error('invalid pin')
    const b64PubKey = await userAPIStore.getUserPubKey()
    if (!b64PubKey) throw new Error('No Publick Key')

    const { DEK, encryptedDEK: b64SKey } = createDataEncryptionKey(
      b64PubKey.pubKey,
    )

    const b64Data = encryptUVD(DEK, {
      UVD: uvd,
      nonce,
    })

    return { b64Data, b64SKey, DEK }
  }

  const createWallet = async ({
    label,
    pin,
    mnemonic,
    account,
  }: {
    label: string
    pin: string
    mnemonic?: string
    account?: {
      currencyId: string
      label: string
    }
  }) => {
    const uvd = makeUVD(pin)
    if (!uvd) throw new Error('invalid pin')
    const b64PubKey = await userAPIStore.getUserPubKey()
    if (!b64PubKey) throw new Error('No Public Key')

    const data = makeHint(pin)
    const dataEncryptionKey = createDataEncryptionKey(b64PubKey.pubKey)
    let DEK = dataEncryptionKey.DEK
    const b64SKey = dataEncryptionKey.encryptedDEK

    const b64Data = encryptUVD(DEK, {
      UVD: uvd,
      nonce: 0,
    })
    const defaultWallet = walletList.value.length === 0

    const params: ICreateWalletRequest = {
      wallet: {
        label,
        defaultWallet,
      },
      account,
      UVD: {
        b64Data: b64Data,
        b64SKey: b64SKey,
      },
      hint: data,
    }

    if (mnemonic) {
      if (!DEK) {
        throw new Error('fail to enc Mnemonic ')
      }
      params.mnemonic = encryptMnemonic(DEK, mnemonic)
      //Clear DEK
      DEK = ''
    }
    return walletAPIStore.createWallet(params)
  }

  const backupWallet = async (
    walletId: string,
    pin: string,
    otpCode?: IVerifyCodeRequest,
  ) => {
    const nonce = await getNonce(walletId)
    if (typeof nonce === 'undefined') {
      throw new LoadNonceError()
    }
    const encUvd = await encryptPINIncluedDEK(pin, nonce)
    if (!encUvd.DEK) {
      throw new Error('fail to make secure channel')
    }
    const b64Mnemonic = await walletAPIStore.backupWallet({
      // walletId: selectedWalletId.value,
      walletId: walletId,
      UVD: {
        b64Data: encUvd.b64Data,
        b64SKey: encUvd.b64SKey,
      },
      otpCode,
    })
    const mnemonic = decryptMnemonic(encUvd.DEK, b64Mnemonic)
    encUvd.DEK = ''
    return mnemonic
  }

  const deleteWallet = async (
    walletId: string,
    pin: string,
    otpCode?: IVerifyCodeRequest,
    options?: APIStoreDeleteWalletOptions,
  ) => {
    const uvd = makeUVD(pin)
    if (!uvd) throw new Error('invalid pin')
    const b64PubKey = await userAPIStore.getUserPubKey()
    if (!b64PubKey) throw new Error('No Public Key')
    let nonce = await getNonce(walletId)
    if (nonce === undefined) throw new LoadNonceError()

    let verify = createDataEncryptionKey(b64PubKey.pubKey)

    let DEK = verify.DEK
    let b64SKey = verify.encryptedDEK

    let b64Data = encryptUVD(DEK, {
      UVD: uvd,
      nonce: nonce,
    })

    let verifyUVD = {
      b64Data: b64Data,
      b64SKey: b64SKey,
    }

    nonce++

    let del = createDataEncryptionKey(b64PubKey.pubKey)
    DEK = del.DEK
    b64SKey = del.encryptedDEK

    b64Data = encryptUVD(DEK, {
      UVD: uvd,
      nonce: nonce,
    })

    let deleteUVD = {
      b64Data: b64Data,
      b64SKey: b64SKey,
    }

    // V2: V1과 동일하게 PIN verify를 먼저 수행하여 nonce 증가
    await walletAPIStore.verifyPin(walletId, verifyUVD)

    await walletAPIStore.deleteWallet(
      walletId,
      {
        UVD: deleteUVD,
        verifyUVD: verifyUVD,
        deleteUVD: deleteUVD,
        otpCode,
      },
      options,
    )
    nonce = 0
    DEK = ''
    b64SKey = ''
    verify = { DEK: '', encryptedDEK: '' }
    del = { DEK: '', encryptedDEK: '' }
    verifyUVD = { b64Data: '', b64SKey: '' }
    deleteUVD = { b64Data: '', b64SKey: '' }
  }

  const getWallet = (id: Wallet['id']): Maybe<Wallet> => {
    return walletList.value?.find((wallet) => wallet.id === id)
  }
  const getDefaultWallet = (): Maybe<Wallet> => {
    return walletList.value?.find((wallet) => wallet.defaultWallet)
  }

  const setSelectedWalletId = (walletId: IWallet['id']) => {
    selectedWalletId.value = walletId
  }

  const getAccountList = ({ walletId }: { walletId: string }) => {
    const wallet = getWallet(walletId)
    return wallet?.accounts
  }
  const getAccount = ({
    accountId,
  }: {
    accountId: string
  }): Account | undefined => {
    return accountList.value.find(
      (account: Account) => account.id === accountId,
    )
  }

  const getAccountListFilteringByNetwork = ({
    network,
  }: GetAccountListFilteringByNetworkParams): Account[] | undefined => {
    const wallet = getWallet(selectedWalletId.value)

    const filtered = wallet?.accounts.filter((account) => {
      // V2: currencyId로 직접 비교 (예: "KLAYTN", "ETHEREUM")
      // V1 호환: coin.network도 체크
      const accountNetwork = account.coin?.network
      const currencyId = account.currencyId

      // V2에서 account.currencyId === token의 parentCurrency.id
      const match = currencyId === network || accountNetwork === network

      return match
    })

    return filtered
  }

  const getNextAddressPath = async ({
    walletId,
  }: {
    walletId: IWallet['id']
  }) => {
    const accounts: Account[] | undefined = getWallet(walletId)?.accounts
    if (!accounts) return '0'
    const addressPath = accounts.reduce((prev, curr) => {
      return Number(prev.addressPath) > Number(curr.addressPath) ? prev : curr
    }).addressPath
    return (addressPath ? Number(addressPath) + 1 : 0).toString()
  }

  const getTokenList = ({ accountId }: { accountId: IAccount['id'] }) => {
    const account = getAccount({ accountId })
    if (!account) throw new Error("Can't find account")

    return account.tokens
  }

  type GetAccountTokenParamsByAccountTokenId = {
    accountTokenId: string
  }
  type GetAccountTokenParamsByTokenId = {
    accountId: string
    tokenId: number
  }
  const isAccountTokenIdParams = (
    obj: any,
  ): obj is GetAccountTokenParamsByAccountTokenId => {
    return obj.accountTokenId
  }
  const getAccountToken = (
    params:
      | GetAccountTokenParamsByAccountTokenId
      | GetAccountTokenParamsByTokenId,
  ) => {
    if (isAccountTokenIdParams(params)) {
      return getAccountTokenByAccountTokenId(params.accountTokenId)
    } else {
      return getAccountTokenByTokenId(params.accountId, params.tokenId)
    }
  }
  const getAccountTokenByAccountTokenId = (accountTokenId: string) => {
    return accountTokenList.value.find(
      (accountToken) => accountToken.id === accountTokenId,
    )
  }
  const getAccountTokenByTokenId = (accountId: string, tokenId: number) => {
    return getAccount({ accountId })?.tokens?.find(
      (account) => account.tokenId === tokenId,
    )
  }

  type FilterbyWalletID = {
    key: IWallet['id']
    status: 'disabled' | 'loading' | 'refresh'
  }
  const refreshStatus = ref<FilterbyWalletID[]>([])

  const getRefreshStatus = (walletId: IWallet['id']) => {
    const result = refreshStatus.value.find(
      (res: FilterbyWalletID) => res.key === walletId,
    )

    if (result?.status !== undefined) {
      return result.status
    }
    return REFRESH_STATUS_DEFAULT
  }

  const setRefresh = ({
    walletId,
    status,
  }: {
    walletId: IWallet['id']
    status: FilterbyWalletID['status']
  }) => {
    const result = refreshStatus.value.find(
      (res: FilterbyWalletID) => res.key === walletId,
    )
    if (result && status === REFRESH_STATUS_DISABLED) {
      setTimeout(() => {
        result.status = REFRESH_STATUS_DEFAULT
      }, consecusiveReqDelay)
    }
    if (result) {
      return (result.status = status)
    }
    refreshStatus.value.push({
      key: walletId,
      status,
    })
  }

  /**
   * get nonce from backend
   */
  const getNonce = async (walletId?: string) => {
    if (typeof walletId === 'undefined' || walletId === null) return 0
    const response = await walletAPIStore.fetchWalletCoreKeyInfo(walletId!)
    if (!response) return
    return response.nonce
  }

  const getVerifyPin = async (walletId: string, pin: string) => {
    const nonce = await getNonce(walletId)
    if (typeof nonce === 'undefined') {
      throw new LoadNonceError()
    }
    const encUvd = await encryptPIN(pin, nonce)
    const response = await walletAPIStore.verifyPin(walletId, encUvd)
    if (!response) return
    return response
  }

  const reset = () => {
    mnemonic.value = ''
    tempFullAccountList.value = undefined
    tempGroupAccountList.value = undefined
    selectedWalletId.value = ''
  }

  return {
    walletList,
    accountList,
    accountTokenList,
    nftList,
    selectedWalletId,
    setSelectedWalletId,
    sortAccountList,
    getWallet,
    getDefaultWallet,
    getAccountList,
    getAccount,
    getAccountListFilteringByNetwork,
    getNextAddressPath,
    getTokenList,
    getAccountToken,
    getNonce,
    createWallet,
    deleteWallet,
    backupWallet,
    encryptPIN,
    encryptPINIncluedDEK,
    fullAccountList,
    groupAccountList,
    tempFullAccountList,
    tempGroupAccountList,
    setMnemonic,
    getMnemonic,
    refreshStatus,
    getRefreshStatus,
    setRefresh,
    getNft,
    reset,
    getVerifyPin,
  }
})

export { useWalletStore }
